import Notiflix from 'notiflix'
import { useEffect, useState } from 'react'
import {
    FiCalendar,
    FiCheckCircle,
    FiExternalLink,
    FiFileText,
    FiHome,
    FiMail,
    FiPhone,
    FiRefreshCcw,
    FiUser,
} from 'react-icons/fi'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface ViewClientDataProps {
    clientData: any
    closeModal: () => void
}

export const ViewPanacoClientDataModalContent = ({ clientData }: ViewClientDataProps) => {
    const [data, setData] = useState<any>([])
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        if (clientData) {
            setData(clientData)
        }
    }, [clientData])

    const handleRefresh = async () => {
        if (!data?.email) {
            Notiflix.Report.warning('No Email Found', 'Cannot refresh client data without an email.', 'Okay')
            return
        }

        setLoading(true)
        try {
            const apiResponse = await api({
                url: '/search-panaco-client',
                data: { term: data.email },
            })

            const updatedData = apiResponse.data.results[0]
            if (updatedData) {
                setData(updatedData)
                Notiflix.Notify.success('Client data refreshed successfully!')
            } else {
                Notiflix.Report.warning('No Updates Found', 'Client data could not be refreshed.', 'Okay')
            }
        } catch (error) {
            console.error('Error fetching updated client data:', error)
            Notiflix.Report.failure('Error', 'Failed to refresh client data.', 'Okay')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={`${styles.viewClientDataModalContainer}`}>
            <h2>Client Information</h2>

            <div className={styles.dataGrid}>
                {[
                    {
                        label: 'Hydra Reference',
                        value: loading ? 'Loading...' : data.hydra_ref,
                        icon: <FiFileText className="icon" />,
                    },
                    {
                        label: 'Name',
                        value: loading ? 'Loading...' : `${data.title} ${data.firstname} ${data.lastname}`,
                        icon: <FiUser className="icon" />,
                    },
                    {
                        label: 'Address',
                        value: loading ? 'Loading...' : data.raw_address,
                        icon: <FiHome className="icon" />,
                    },
                    {
                        label: 'Date of Birth',
                        value: loading
                            ? 'Loading...'
                            : data?.dob
                            ? data.dob.split('T')[0].split('-').reverse().join('/')
                            : 'N/A',
                        icon: <FiCalendar className="icon" />,
                    },
                    {
                        label: 'Email',
                        value: loading ? 'Loading...' : data.email,
                        icon: <FiMail className="icon" />,
                    },
                    {
                        label: 'Phone',
                        value: loading ? 'Loading...' : data.telephone,
                        icon: <FiPhone className="icon" />,
                    },
                ]
                    .filter(Boolean)
                    .map((item, index) => (
                        <div key={index} className={styles.dataCard}>
                            <div className={styles.label}>
                                {item.icon}
                                <span>{item.label}</span>
                            </div>
                            <b>{item.value}</b>
                        </div>
                    ))}

                <div className={styles.fullWidthCard}>
                    <div className={styles.twoColumnGrid}>
                        <div className={styles.column}>
                            <div className={styles.inlineRow}>
                                <FiCheckCircle className="icon" />
                                <span>CFA Signed:</span>
                                <b>{loading ? 'Loading...' : data.signed ? 'Yes' : 'No'}</b>
                            </div>

                            {data.signed && (
                                <div className={styles.inlineRow}>
                                    <FiCalendar className="icon" />
                                    <span>Signature Date:</span>
                                    <b>
                                        {loading
                                            ? 'Loading...'
                                            : data?.signature_date
                                            ? `${data.signature_date.split('T')[0].split('-').reverse().join('/')} 
                 ${data.signature_date.split('T')[1].slice(0, 5)}`
                                            : 'N/A'}
                                    </b>
                                </div>
                            )}
                        </div>

                        <div className={styles.column}>
                            <div className={styles.inlineRow}>
                                <FiExternalLink className="icon" />
                                <span>Signature Portal:</span>
                                {loading ? (
                                    'Loading...'
                                ) : (
                                    <a
                                        href={`https://portal.sentinellegal.co.uk/cfa-salesone/${data.token}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View Portal
                                    </a>
                                )}
                            </div>

                            <div className={styles.inlineRow}>
                                <FiExternalLink className="icon" />
                                <span>Link Opened:</span>
                                <b>{loading ? 'Loading...' : data.link_opened ? 'Yes' : 'No'}</b>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <button onClick={handleRefresh} disabled={loading}>
                {loading ? <FiRefreshCcw className="icon-spin" /> : 'Refresh Data'}
            </button>
        </div>
    )
}
